import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IIssue } from '@d-orca/shared/trusstor';
import { useQuickStatusUpdate } from '@components/Pages/IssuesPage/hooks';
import { issueStatusAction } from '@d-orca/shared/client/issues';
import { IssueReviewDialog } from './IssueReviewDialog';
import styles from './issueStatus.module.css';

export interface IIssueStatusControlProps {
  issue: Pick<IIssue, '_id' | 'status'>;
  siteId: string | null;
  /** The viewer holds `issues.manage` — picks the manager column of the transition table. */
  canManage: boolean;
  /** Placement class for the wrapper. */
  className?: string;
}

/**
 * An issue's single status control, on the card and in the drawer alike — its label and move come from
 * `issueStatusAction`, so the two surfaces can't drift. Not rendered when the user has no move (a contributor on a
 * completed issue). A manager on an issue waiting for review gets Review, which opens the review dialog.
 *
 * Clicks and keys stop at the wrapper — including the ones from the dialog, which portals out of the DOM but still
 * bubbles through the React tree — so acting here never also opens the card it sits on.
 */
export function IssueStatusControl({ issue, siteId, canManage, className }: IIssueStatusControlProps) {
  const { t } = useTranslation();
  const quickStatus = useQuickStatusUpdate(siteId);
  const [reviewing, setReviewing] = useState(false);
  const action = issueStatusAction(issue.status, canManage);
  if (!action) return null;

  return (
    <span className={className} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      {action === 'review' ? (
        <>
          <button type="button" className={styles.action} onClick={() => setReviewing(true)}>
            {t('activities.issueStatus.review')}
          </button>
          {reviewing && (
            <IssueReviewDialog onClose={() => setReviewing(false)} siteId={siteId} issueId={issue._id} />
          )}
        </>
      ) : (
        <button
          type="button"
          className={styles.action}
          disabled={quickStatus.isPending}
          onClick={() => quickStatus.mutate({ issueId: issue._id, status: action.next })}
        >
          {t(action.labelKey)}
        </button>
      )}
    </span>
  );
}
